import { collection, query, where, orderBy, limit, getDocs, onSnapshot, Timestamp } from 'firebase/firestore';
import { POD_COLLECTION } from '../config/collections';
import { getLatestMeasurements, getAllMeasurements } from './podMeasurements';
import { db } from '../lib/firebase';
import { ClearAdminBillingData } from '../types/clearAdmin';

// Enhanced types for Energia CRM with detailed analytics
export interface EnergiaPOD {
  id: string;
  pod_id: string;
  provider: string;
  last_update: Timestamp;
  linked_customer_id?: string;
  status?: string;
  total_consumption_kwh: number;
  total_reverse_feed_kwh?: number; // Visszatáplálás (napelem, megújuló energia)
  total_cost_huf: number;
  average_price_huf_kwh: number;
  measurement_count: number;
  date_range: {
    start: Timestamp;
    end: Timestamp;
  };
  // Customer information from CRM
  customer_info?: {
    customer_id?: string;
    customer_name?: string;
    company_name?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    tax_number?: string;
    customer_type?: 'individual' | 'organization';
    is_active?: boolean;
  };
  data_quality?: {
    total_measurements: number;
    missing_data_percentage: number;
    data_completeness: 'good' | 'limited' | 'poor';
  };
  peak_analysis?: {
    peak_hour: number;
    low_hour: number;
    peak_day_of_week: number;
    peak_consumption_kwh: number;
    min_consumption_kwh: number;
  };
  patterns?: {
    hourly_averages: Record<string, number>;
    daily_averages: Record<string, number>;
  };
  trends?: {
    recent_30_day_avg: number;
    trend_percentage: number;
    trend_direction: 'increasing' | 'decreasing' | 'stable';
  };
  statistics?: {
    avg_daily_consumption: number;
    max_daily_consumption: number;
    min_daily_consumption: number;
    std_daily_consumption: number;
  };
}

export interface EnergiaMeasurement {
  timestamp: Timestamp;
  consumption: number;
  reverse_feed?: number; // New: separate reverse feed field (backward compatible)
  price_huf_kwh: number;
  cost_huf: number;
  upload_date: Timestamp;
  metadata?: {
    hour: number;
    day_of_week: number;
    is_weekend: boolean;
    is_peak_hour: boolean;
    season: 'winter' | 'spring' | 'summer' | 'autumn';
  };
}

export interface EnergiaDailySummary {
  date: Timestamp;
  total_consumption_kwh: number;
  total_reverse_feed_kwh?: number;
  total_cost_huf: number;
  avg_price_huf_kwh: number;
  upload_date: Timestamp;
}

export interface EnergiaMonthlySummary {
  month: string; // YYYY-MM format
  year: number;
  monthNumber: number;
  total_consumption_kwh: number;
  total_reverse_feed_kwh: number;
  total_cost_huf: number;
  avg_price_huf_kwh: number;
  days_count: number;
  daily_avg_consumption: number;
}

export interface MSCONSBillingRecord {
  id: string;
  pod_id: string;
  provider: string;
  billing_period_start: string; // YYYYMMDD format
  billing_period_end: string; // YYYYMMDD format
  total_consumption_kwh: number;
  consumption_day_kwh?: number; // Day period (6-17)
  consumption_peak_kwh?: number; // Peak period (17-22)
  consumption_valley_kwh?: number; // Valley period (22-06)
  data_source: string; // 'MSCONS'
  xml_file?: string;
  upload_timestamp: Timestamp | string;
}

export interface EnergiaBill {
  id: string;
  pod_id: string;
  customer_name: string;
  billing_period: string;
  total_consumption_kwh: number;
  total_cost_huf: number;
  average_price_huf_kwh: number;
  provider: string;
  upload_timestamp: Timestamp;
  billing_status: 'pending' | 'processed' | 'sent' | 'paid';
  processed_by: string;
  billing_metadata?: {
    auto_generated: boolean;
    data_source: string;
    processing_session: string;
    bill_type: string;
    currency: string;
    energy_unit: string;
  };
  customer_info?: {
    customer_id?: string;
    customer_name?: string;
    company_name?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    tax_number?: string;
    customer_type?: 'individual' | 'organization';
    is_active?: boolean;
  };
}

export interface ProviderSummary {
  provider: string;
  total_customers: number;
  total_consumption_kwh: number;
  total_cost_huf: number;
  processing_date: string;
  upload_timestamp: Timestamp;
  status: string;
  last_processing_date?: string;
  data_freshness?: string;
  processing_statistics?: {
    avg_consumption_per_customer: number;
    total_processing_time_minutes: number;
    data_quality_score: number;
    unique_pods: number;
    time_period_days: number;
  };
}

export interface ProcessingLog {
  provider: string;
  session_id: string;
  timestamp: Timestamp;
  processing_details: {
    status: string;
    start_time: string;
    files_processed: number;
    records_processed: number;
    customers_processed: number;
    total_consumption_kwh: number;
    total_cost_huf: number;
    average_price_huf_kwh: number;
    data_quality_metrics: {
      completeness_percentage: number;
      valid_measurements: number;
      invalid_measurements: number;
      duplicate_measurements: number;
    };
    performance_metrics: {
      processing_duration_seconds: number;
      records_per_second: number;
      memory_usage_mb: number;
    };
  };
  status: string;
}

export interface SystemStatus {
  provider: string;
  status: 'processing' | 'completed' | 'error' | 'critical_error';
  timestamp: Timestamp;
  details: Record<string, any>;
  monitoring?: {
    uptime_status: 'operational' | 'processing' | 'down';
    last_successful_run?: Timestamp;
    consecutive_failures: number;
    health_score: number;
  };
}

class EnergiaService {
  
  /**
   * Get all PODs from energy_pods collection with optional provider filter and enhanced analytics
   * Also attempts to match with CRM customer data
   */
  async getCustomers(provider?: string): Promise<EnergiaPOD[]> {
    try {
      let q = query(collection(db, POD_COLLECTION), orderBy('last_update', 'desc'));
      
      if (provider) {
        q = query(collection(db, POD_COLLECTION), where('provider', '==', provider), orderBy('last_update', 'desc'));
      }
      
      const snapshot = await getDocs(q);
      const energiaPods: EnergiaPOD[] = [];
      
      snapshot.forEach((doc) => {
        energiaPods.push({
          id: doc.id,
          ...doc.data()
        } as EnergiaPOD);
      });
      
      // Attempt to enrich with CRM customer data
      await this.enrichWithCrmCustomerData(energiaPods);
      
      return energiaPods;
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  /**
   * Enrich energia PODs with CRM customer data
   */
  private async enrichWithCrmCustomerData(energiaPods: EnergiaPOD[]): Promise<void> {
    try {
      // Get CRM customers from customers collection (those without provider field)
      const crmCustomersRef = collection(db, 'customers');
      const crmSnapshot = await getDocs(crmCustomersRef);
      
      // Create a map of customer ID to customer data for linked_customer_id matching
      const customerIdToDataMap = new Map<string, any>();
      // Create a map of POD ID to customer data for pod.id matching (legacy)
      const podToCustomerMap = new Map<string, any>();
      
      crmSnapshot.forEach((doc) => {
        const customerData = doc.data();
        
        // Skip energia customers (those with provider field) - we want only CRM customers
        if (customerData.provider) {
          return; // This is an energia customer, skip it
        }
        
        const customerInfo = {
          customer_id: doc.id,
          customer_name: customerData.companyName || `${customerData.firstName} ${customerData.lastName}`.trim(),
          company_name: customerData.companyName,
          contact_email: customerData.email,
          contact_phone: customerData.phone,
          address: customerData.address ? 
            `${customerData.address.street}, ${customerData.address.city} ${customerData.address.postalCode}` : 
            undefined,
          tax_number: customerData.taxNumber,
          customer_type: customerData.type,
          is_active: customerData.isActive
        };
        
        // Map by customer ID for linked_customer_id matching
        customerIdToDataMap.set(doc.id, customerInfo);
        
        // Legacy: Check main POD for CRM customers (pod.id field)
        if (customerData.pod?.id) {
          podToCustomerMap.set(customerData.pod.id, customerInfo);
        }
        
        // Legacy: Check additional PODs for CRM customers
        if (customerData.additionalPods && Array.isArray(customerData.additionalPods)) {
          customerData.additionalPods.forEach((pod: any) => {
            if (pod.id) {
              podToCustomerMap.set(pod.id, {
                ...customerInfo,
                address: pod.address || customerInfo.address
              });
            }
          });
        }
      });
      
      // Enrich energia PODs with CRM customer data or default data
      energiaPods.forEach(pod => {
        let customerInfo = null;
        
        // First, try to find customer by linked_customer_id (new way)
        if ((pod as any).linked_customer_id) {
          customerInfo = customerIdToDataMap.get((pod as any).linked_customer_id);
        }
        
        // Fallback: try to find customer by POD ID (legacy way)
        if (!customerInfo) {
          customerInfo = podToCustomerMap.get(pod.pod_id);
        }
        
        if (customerInfo) {
          // Found matching CRM customer
          pod.customer_info = customerInfo;
        } else {
          // No CRM customer found, show as unassigned
          pod.customer_info = {
            customer_id: pod.id,
            customer_name: "Nincs hozzárendelve",
            company_name: "Nincs hozzárendelve",
            customer_type: 'organization',
            is_active: false
          };
        }
      });
      
    } catch (error) {
      console.error('Error enriching with CRM customer data:', error);
      // Don't throw error, just continue without enrichment
    }
  }

  /**
   * Get measurements for a specific POD with enhanced metadata
   */
  async getPodMeasurements(podId: string, limitCount: number = 1000): Promise<EnergiaMeasurement[]> {
    try {
      // v1: measurements alkollekció; v2: napi values[] tömbök kibontva (podMeasurements adapter)
      return (await getLatestMeasurements(podId, limitCount)) as EnergiaMeasurement[];
    } catch (error) {
      console.error('Error fetching POD measurements:', error);
      throw error;
    }
  }

  /**
   * Get all measurements for a specific POD (for detailed analysis)
   */
  async getAllPodMeasurements(podId: string): Promise<EnergiaMeasurement[]> {
    try {
      return (await getAllMeasurements(podId)) as EnergiaMeasurement[];
    } catch (error) {
      console.error('Error fetching all POD measurements:', error);
      throw error;
    }
  }

  /**
   * Get daily summaries for a specific POD
   */
  async getPodDailySummaries(podId: string, limitCount: number = 60): Promise<EnergiaDailySummary[]> {
    try {
      const dailyRef = collection(db, POD_COLLECTION, podId, 'daily_summaries');
      const q = query(dailyRef, orderBy('date', 'desc'), limit(limitCount));
      
      const snapshot = await getDocs(q);
      const summaries: EnergiaDailySummary[] = [];
      
      snapshot.forEach((doc) => {
        summaries.push(doc.data() as EnergiaDailySummary);
      });
      
      return summaries;
    } catch (error) {
      console.error('Error fetching POD daily summaries:', error);
      throw error;
    }
  }

  /**
   * Get monthly summaries for a specific POD (aggregated from daily summaries)
   */
  async getMonthlyPodSummaries(podId: string, monthsCount: number = 12): Promise<EnergiaMonthlySummary[]> {
    try {
      // Fetch enough daily summaries to cover the requested months
      const daysToFetch = monthsCount * 31; // Max days per month
      const dailySummaries = await this.getPodDailySummaries(podId, daysToFetch);
      
      if (dailySummaries.length === 0) {
        return [];
      }
      
      // Check if daily summaries already have reverse_feed data
      const hasReverseFeedInSummaries = dailySummaries.some(s => (s.total_reverse_feed_kwh || 0) > 0);
      
      // Only fetch raw measurements if daily summaries don't have reverse_feed data
      let measurements: any[] = [];
      if (!hasReverseFeedInSummaries) {
        measurements = await getLatestMeasurements(podId, 5000);
      }
      
      // Group by month
      const monthlyData = new Map<string, {
        consumption: number;
        reverse_feed: number;
        cost: number;
        days: number;
      }>();
      
      dailySummaries.forEach(summary => {
        const date = summary.date.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        const existing = monthlyData.get(monthKey) || {
          consumption: 0,
          reverse_feed: 0,
          cost: 0,
          days: 0
        };
        
        existing.consumption += summary.total_consumption_kwh || 0;
        existing.reverse_feed += summary.total_reverse_feed_kwh || 0;
        existing.cost += summary.total_cost_huf || 0;
        existing.days += 1;
        
        monthlyData.set(monthKey, existing);
      });
      
      // Calculate reverse feed from raw measurements for all months (only if we fetched measurements)
      // BACKWARD COMPATIBLE: Supports both old (negative consumption) and new (separate reverse_feed field) structures
      if (measurements.length > 0) {
        monthlyData.forEach((data, monthKey) => {
          const [year, month] = monthKey.split('-').map(Number);
          
          const reverseFeedSum = measurements
            .filter(m => {
              const mDate = m.timestamp?.toDate();
              return mDate && 
                     mDate.getFullYear() === year && 
                     mDate.getMonth() + 1 === month;
            })
            .reduce((sum, m) => {
              // New structure: use separate reverse_feed field if available
              if (m.reverse_feed !== undefined && m.reverse_feed > 0) {
                return sum + m.reverse_feed;
              }
              // Old structure: negative consumption = reverse feed
              if ((m.consumption || 0) < 0) {
                return sum + Math.abs(m.consumption || 0);
              }
              return sum;
            }, 0);
          
          // Always use calculated value from measurements (more accurate)
          if (reverseFeedSum > 0) {
            data.reverse_feed = reverseFeedSum;
          }
        });
      }
      
      // Convert to array and sort by month (newest first)
      const monthlySummaries: EnergiaMonthlySummary[] = Array.from(monthlyData.entries())
        .map(([monthKey, data]) => {
          const [year, month] = monthKey.split('-').map(Number);
          return {
            month: monthKey,
            year,
            monthNumber: month,
            total_consumption_kwh: data.consumption,
            total_reverse_feed_kwh: data.reverse_feed,
            total_cost_huf: data.cost,
            avg_price_huf_kwh: data.consumption > 0 ? data.cost / data.consumption : 0,
            days_count: data.days,
            daily_avg_consumption: data.days > 0 ? data.consumption / data.days : 0
          };
        })
        .sort((a, b) => b.month.localeCompare(a.month))
        .slice(0, monthsCount);
      
      return monthlySummaries;
    } catch (error) {
      console.error('Error calculating monthly POD summaries:', error);
      throw error;
    }
  }

  /**
   * Get processing logs for monitoring
   */
  async getProcessingLogs(provider?: string, limitCount: number = 50): Promise<ProcessingLog[]> {
    try {
      let q = query(collection(db, 'processing_logs'), orderBy('timestamp', 'desc'), limit(limitCount));
      
      if (provider) {
        q = query(collection(db, 'processing_logs'), where('provider', '==', provider), orderBy('timestamp', 'desc'), limit(limitCount));
      }
      
      const snapshot = await getDocs(q);
      const logs: ProcessingLog[] = [];
      
      snapshot.forEach((doc) => {
        logs.push({
          ...doc.data()
        } as ProcessingLog);
      });
      
      return logs;
    } catch (error) {
      console.error('Error fetching processing logs:', error);
      throw error;
    }
  }

  /**
   * Get MSCONS billing records for a specific POD
   */
  async getMSCONSBillingRecords(podId: string): Promise<MSCONSBillingRecord[]> {
    try {
      // New structure: energy_pods/{pod_id}/mscons/{period}
      const msConsRef = collection(db, POD_COLLECTION, podId, 'mscons');
      const snapshot = await getDocs(msConsRef);
      
      const records: MSCONSBillingRecord[] = [];
      
      snapshot.forEach((doc) => {
        records.push({
          id: doc.id,
          ...doc.data()
        } as MSCONSBillingRecord);
      });
      
      // Sort by period (document ID is the period like 2026-02)
      records.sort((a, b) => b.id.localeCompare(a.id));
      
      return records;
    } catch (error) {
      console.error('Error fetching MSCONS billing records:', error);
      return [];
    }
  }

  /**
   * Get monthly summaries from Firebase (pre-calculated by backend)
   * New structure: energy_pods/{pod_id}/monthly/{period}
   */
  async getMonthlyPodSummariesFromFirebase(podId: string): Promise<any[]> {
    try {
      // New structure: energy_pods/{pod_id}/monthly/
      const monthlyRef = collection(db, POD_COLLECTION, podId, 'monthly');
      const snapshot = await getDocs(monthlyRef);
      
      const records: any[] = [];
      
      snapshot.forEach((doc) => {
        records.push({
          id: doc.id,
          period: doc.id, // Document ID is the period (2026-02)
          ...doc.data()
        });
      });
      
      // Sort by period (newest first)
      records.sort((a, b) => b.id.localeCompare(a.id));
      
      return records;
    } catch (error) {
      console.error('Error fetching monthly summaries from Firebase:', error);
      return [];
    }
  }

  /**
   * Get billing queue with specific status and enhanced customer data
   * New structure: energy_pods/{pod_id}/mscons/{period}
   */
  async getBillingQueue(status: string = 'pending'): Promise<EnergiaBill[]> {
    try {
      // Get all PODs
      const podsRef = collection(db, POD_COLLECTION);
      const podsSnapshot = await getDocs(podsRef);
      
      const bills: EnergiaBill[] = [];
      
      // For each POD, get mscons subcollection
      for (const podDoc of podsSnapshot.docs) {
        const podId = podDoc.id;
        const msConsRef = collection(db, POD_COLLECTION, podId, 'mscons');
        const msConsSnapshot = await getDocs(msConsRef);
        
        msConsSnapshot.forEach((doc) => {
          const data = doc.data();
          // Filter by status if needed
          if (!status || data.billing_status === status) {
            bills.push({
              id: `${podId}_${doc.id}`,
              pod_id: podId,
              period: doc.id,
              ...data
            } as any as EnergiaBill);
          }
        });
      }
      
      // Sort by upload_timestamp descending
      bills.sort((a, b) => {
        const aTime = a.upload_timestamp?.toMillis?.() || 0;
        const bTime = b.upload_timestamp?.toMillis?.() || 0;
        return bTime - aTime;
      });

      // Attempt to enrich with CRM customer data
      await this.enrichBillsWithCrmCustomerData(bills);
      
      return bills;
    } catch (error) {
      console.error('Error fetching billing queue:', error);
      throw error;
    }
  }

  /**
   * Enrich billing data with CRM customer data
   */
  private async enrichBillsWithCrmCustomerData(bills: EnergiaBill[]): Promise<void> {
    try {
      // Get CRM customers from same collection (those with pod.id field, not provider field)
      const crmCustomersRef = collection(db, 'customers');
      const crmSnapshot = await getDocs(crmCustomersRef);
      
      // Create a map of POD ID to customer data
      const podToCustomerMap = new Map<string, any>();
      
      crmSnapshot.forEach((doc) => {
        const customerData = doc.data();
        
        // Skip energia customers (those with provider field) - we want only CRM customers
        if (customerData.provider) {
          return; // This is an energia customer, skip it
        }
        
        // Check main POD for CRM customers
        if (customerData.pod?.id) {
          podToCustomerMap.set(customerData.pod.id, {
            customer_id: doc.id,
            customer_name: customerData.companyName || `${customerData.firstName} ${customerData.lastName}`.trim(),
            company_name: customerData.companyName,
            contact_email: customerData.email,
            contact_phone: customerData.phone,
            address: customerData.address ? 
              `${customerData.address.street}, ${customerData.address.city} ${customerData.address.postalCode}` : 
              undefined,
            tax_number: customerData.taxNumber,
            customer_type: customerData.type,
            is_active: customerData.isActive
          });
        }
        
        // Check additional PODs for CRM customers
        if (customerData.additionalPods && Array.isArray(customerData.additionalPods)) {
          customerData.additionalPods.forEach((pod: any) => {
            if (pod.id) {
              podToCustomerMap.set(pod.id, {
                customer_id: doc.id,
                customer_name: customerData.companyName || `${customerData.firstName} ${customerData.lastName}`.trim(),
                company_name: customerData.companyName,
                contact_email: customerData.email,
                contact_phone: customerData.phone,
                address: pod.address || (customerData.address ? 
                  `${customerData.address.street}, ${customerData.address.city} ${customerData.address.postalCode}` : 
                  undefined),
                tax_number: customerData.taxNumber,
                customer_type: customerData.type,
                is_active: customerData.isActive
              });
            }
          });
        }
      });
      
      // Enrich bills with CRM customer data or default data
      bills.forEach(bill => {
        const customerInfo = podToCustomerMap.get(bill.pod_id);
        if (customerInfo) {
          // Found matching CRM customer
          bill.customer_info = customerInfo;
        } else {
          // No CRM customer found, show as unassigned
          bill.customer_info = {
            customer_id: bill.pod_id,
            customer_name: "Nincs hozzárendelve",
            company_name: "Nincs hozzárendelve",
            customer_type: 'organization',
            is_active: false
          };
        }
      });
      
    } catch (error) {
      console.error('Error enriching bills with CRM customer data:', error);
      // Don't throw error, just continue without enrichment
    }
  }

  /**
   * Get enhanced provider summaries with monthly data
   */
  async getProviderSummaries(): Promise<ProviderSummary[]> {
    try {
      const providersRef = collection(db, 'providers');
      const snapshot = await getDocs(providersRef);
      const summaries: ProviderSummary[] = [];
      
      for (const providerDoc of snapshot.docs) {
        const provider = providerDoc.id;
        
        // Get latest daily summary
        const dailySummariesRef = collection(providerDoc.ref, 'daily_summaries');
        const latestSummaryQuery = query(dailySummariesRef, orderBy('upload_timestamp', 'desc'), limit(1));
        const latestSnapshot = await getDocs(latestSummaryQuery);
        
        if (!latestSnapshot.empty) {
          const latestSummary = latestSnapshot.docs[0].data();
          summaries.push({
            provider,
            ...latestSummary
          } as ProviderSummary);
        }
      }
      
      return summaries;
    } catch (error) {
      console.error('Error fetching provider summaries:', error);
      throw error;
    }
  }

  /**
   * Get monthly provider summaries for trends
   */
  async getMonthlyProviderSummaries(provider: string): Promise<any[]> {
    try {
      const monthlyRef = collection(db, 'providers', provider, 'monthly_summaries');
      const q = query(monthlyRef, orderBy('last_update', 'desc'), limit(12));
      
      const snapshot = await getDocs(q);
      const monthlySummaries: any[] = [];
      
      snapshot.forEach((doc) => {
        monthlySummaries.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return monthlySummaries;
    } catch (error) {
      console.error('Error fetching monthly summaries:', error);
      throw error;
    }
  }

  /**
   * Get system status with enhanced monitoring
   */
  async getSystemStatus(): Promise<SystemStatus[]> {
    try {
      const q = query(collection(db, 'system_status'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const statuses: SystemStatus[] = [];
      
      snapshot.forEach((doc) => {
        statuses.push({
          provider: doc.id,
          ...doc.data()
        } as SystemStatus);
      });
      
      return statuses;
    } catch (error) {
      console.error('Error fetching system status:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time POD updates with analytics
   */
  subscribeToCustomers(provider: string | null, callback: (customers: EnergiaPOD[]) => void) {
    let q = query(collection(db, POD_COLLECTION), orderBy('last_update', 'desc'), limit(500));
    
    if (provider) {
      q = query(collection(db, POD_COLLECTION), where('provider', '==', provider), orderBy('last_update', 'desc'), limit(500));
    }
    
    return onSnapshot(q, async (snapshot) => {
      const energiaPods: EnergiaPOD[] = [];
      snapshot.forEach((doc) => {
        energiaPods.push({
          id: doc.id,
          ...doc.data()
        } as EnergiaPOD);
      });
      
      // Enrich with CRM customer data
      await this.enrichWithCrmCustomerData(energiaPods);
      
      callback(energiaPods);
    });
  }

  /**
   * Subscribe to real-time billing queue updates
   * NOTE: Removed in favor of new structure (energy_pods/{pod_id}/mscons/)
   * Use getBillingQueue() with polling instead for better performance
   */
  subscribeToBillingQueue(status: string, callback: (bills: EnergiaBill[]) => void) {
    console.warn('subscribeToBillingQueue is deprecated - use getBillingQueue() with polling instead');
    // Return empty unsubscribe function
    return () => {};
  }

  /**
   * Subscribe to real-time system status updates
   */
  subscribeToSystemStatus(callback: (statuses: SystemStatus[]) => void) {
    const q = query(collection(db, 'system_status'), orderBy('timestamp', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const statuses: SystemStatus[] = [];
      snapshot.forEach((doc) => {
        statuses.push({
          provider: doc.id,
          ...doc.data()
        } as SystemStatus);
      });
      callback(statuses);
    });
  }

  /**
   * Get consumption statistics for a customer
   */
  getConsumptionStats(pod: EnergiaPOD) {
    return {
      dailyAverage: pod.statistics?.avg_daily_consumption || 0,
      peakHour: pod.peak_analysis?.peak_hour || 0,
      trendDirection: pod.trends?.trend_direction || 'stable',
      trendPercentage: pod.trends?.trend_percentage || 0,
      dataQuality: pod.data_quality?.data_completeness || 'unknown',
      seasonalPattern: this.getSeasonalPattern(pod.patterns?.hourly_averages)
    };
  }

  /**
   * Analyze seasonal consumption patterns
   */
  private getSeasonalPattern(hourlyAverages?: Record<string, number>) {
    if (!hourlyAverages) return 'unknown';
    
    const values = Object.values(hourlyAverages);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const variation = (max - min) / max;
    
    if (variation > 0.5) return 'high_variation';
    if (variation > 0.3) return 'medium_variation';
    return 'low_variation';
  }

  /**
   * Format Hungarian currency
   */
  formatHUF(amount: number): string {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Format energy consumption
   */
  formatKWH(amount: number): string {
    return new Intl.NumberFormat('hu-HU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' kWh';
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number): string {
    return new Intl.NumberFormat('hu-HU', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value / 100);
  }

  /**
   * Get trend icon based on direction
   */
  getTrendIcon(direction: string): string {
    switch (direction) {
      case 'increasing': return '📈';
      case 'decreasing': return '📉';
      case 'stable': return '➡️';
      default: return '❓';
    }
  }

  /**
   * Get data quality color
   */
  getDataQualityColor(quality: string): string {
    switch (quality) {
      case 'good': return 'text-green-600';
      case 'limited': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }

  /**
   * Get POD data from energy_pods collection
   * New structure: All data is under energy_pods/{pod_id}/
   */
  async getHybridPods(): Promise<EnergiaPOD[]> {
    try {
      const hybridPods: EnergiaPOD[] = [];
      
      // Get PODs from energy_pods collection
      const energyPodsQuery = query(collection(db, POD_COLLECTION), orderBy('last_update', 'desc'));
      const energyPodsSnapshot = await getDocs(energyPodsQuery);
      
      energyPodsSnapshot.forEach((doc) => {
        const podData = {
          id: doc.id,
          ...doc.data()
        } as EnergiaPOD;
        hybridPods.push(podData);
      });
      
      // Enrich with CRM customer data
      await this.enrichWithCrmCustomerData(hybridPods);
      
      return hybridPods;
      
    } catch (error) {
      console.error('Error fetching hybrid POD data:', error);
      throw error;
    }
  }

  /**
   * Get billing data for Clear Admin export
   */
  async getBillingDataForClearAdmin(
    provider?: string,
    month?: number,
    year?: number
  ): Promise<ClearAdminBillingData[]> {
    try {
      let billsQuery = query(collection(db, 'energia_bills'), orderBy('billing_period', 'desc'));
      
      if (provider) {
        billsQuery = query(
          collection(db, 'energia_bills'),
          where('provider', '==', provider),
          orderBy('billing_period', 'desc')
        );
      }
      
      const snapshot = await getDocs(billsQuery);
      const bills: EnergiaBill[] = [];
      
      snapshot.forEach((doc) => {
        bills.push({
          id: doc.id,
          ...doc.data()
        } as EnergiaBill);
      });

      // Filter by month/year if specified
      let filteredBills = bills;
      if (month && year) {
        filteredBills = bills.filter(bill => {
          const billingDate = new Date(bill.billing_period);
          return billingDate.getMonth() + 1 === month && billingDate.getFullYear() === year;
        });
      }

      // Convert to ClearAdminBillingData format
      const clearAdminData: ClearAdminBillingData[] = filteredBills.map(bill => {
        const customerInfo = bill.customer_info;
        
        // Calculate amounts if not present
        const grossAmount = bill.total_cost_huf || 0;
        const vatRate = 0.27; // 27% VAT
        const netAmount = grossAmount / (1 + vatRate);
        const vatAmount = grossAmount - netAmount;
        const pricePerKwh = bill.total_consumption_kwh > 0 ? grossAmount / bill.total_consumption_kwh : 0;

        return {
          customer_id: customerInfo?.customer_id || bill.id,
          customer_name: customerInfo?.customer_name || bill.customer_name || 'Ismeretlen ügyfél',
          pod_id: bill.pod_id,
          provider: bill.provider,
          billing_period: bill.billing_period,
          period_start: bill.billing_period, // Use billing_period as fallback
          period_end: bill.billing_period,   // Use billing_period as fallback
          consumption_kwh: bill.total_consumption_kwh || 0,
          price_per_kwh: pricePerKwh,
          net_amount: netAmount,
          vat_amount: vatAmount,
          gross_amount: grossAmount,
          tax_number: customerInfo?.tax_number || '',
          address: customerInfo?.address || '',
          contact_email: customerInfo?.contact_email || '',
          contact_phone: customerInfo?.contact_phone || '',
          billing_status: bill.billing_status || 'active'
        };
      });

      return clearAdminData;
      
    } catch (error) {
      console.error('Error fetching billing data for Clear Admin:', error);
      throw error;
    }
  }
}

export const energiaService = new EnergiaService(); 